// ReportTableBody.jsx
import React, { useState, useEffect } from "react";
import {
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  IconButton,
  Collapse,
  Box,
  Tabs,
  Tab,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Stack,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  GetApp as DownloadIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as ProcessingIcon,
  Image as ImageIcon,
  TableChart as TableChartIcon,
  DataObject as DataObjectIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Article as ArticleIcon,
  FolderZip as FolderZipIcon,
  InsertDriveFile as FileIcon,
  Biotech as BiotechIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarTodayIcon,
  FileDownload as FileDownloadIcon,
  BarChart as BarChartIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Input as InputIcon,
  Cancel as CancelIcon,
  AutoAwesome as AutoAwesomeIcon,
  SmartToy as SmartToyIcon,
} from "@mui/icons-material";
import { formatDate } from "./DateUtils";
import apiClient from "./utils/apiClient";
import { useAuthStore } from "./store/authStore";
import FileViewer from "./FileViewer";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import yaml from "js-yaml";
import { productIdToTitle } from "./ProductUtils";
import PresentationViewer from "./PresentationViewer";
import BioInterpreterEvaluationPanel from "./BioInterpreterEvaluationPanel";
import { formatUsage } from "./utils/usage";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  completed:  { color: "#10B981", bg: "#ECFDF5", label: "Completed",  Icon: CheckCircleIcon },
  error:      { color: "#EF4444", bg: "#FEF2F2", label: "Error",      Icon: ErrorIcon },
  processing: { color: "#F59E0B", bg: "#FFFBEB", label: "Processing", Icon: ProcessingIcon },
  started:    { color: "#3B82F6", bg: "#EFF6FF", label: "Running",    Icon: ProcessingIcon },
  stopped:    { color: "#64748B", bg: "#F1F5F9", label: "Stopped",    Icon: CancelIcon },
  cancelled:  { color: "#64748B", bg: "#F1F5F9", label: "Cancelled",  Icon: CancelIcon },
};

// Statuses that mean "this run isn't going anywhere else" — used to gate the
// Cancel action. A blocklist, not an allowlist: in-flight status is many
// free-text strings ("process 2 of 5", "converting presentation", "processing
// BioInterpreter", ...) that are impractical to enumerate.
const TERMINAL_REPORT_STATUSES = new Set(["completed", "error", "stopped", "cancelled"]);

function getStatusConfig(status) {
  if (!status) return STATUS_CONFIG.processing;
  const lower = status.toLowerCase();
  if (STATUS_CONFIG[lower]) return STATUS_CONFIG[lower];
  if (lower.startsWith("process")) return { ...STATUS_CONFIG.processing, label: status };
  if (lower.startsWith("started")) return { ...STATUS_CONFIG.started, label: status };
  return { color: "#64748B", bg: "#F1F5F9", label: status, Icon: ProcessingIcon };
}

function getDuration(start, end) {
  if (!start) return null;
  const toMs = (v) => (Number(v) < 1e12 ? Number(v) * 1000 : Number(v));
  const startMs = toMs(start);
  const inProgress = end == null || end === "";
  const endMs = inProgress ? Date.now() : toMs(end);
  let s = Math.max(0, Math.floor((endMs - startMs) / 1000));
  const h = Math.floor(s / 3600); s -= h * 3600;
  const m = Math.floor(s / 60);   s -= m * 60;
  const label = h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`;
  return inProgress ? `${label} (running)` : label;
}

const FILE_TYPE_RULES = [
  { exts: ["png", "jpg", "jpeg", "gif", "svg"],        Icon: ImageIcon,        color: "#8B5CF6", label: "Images" },
  { exts: ["xlsx", "csv", "tsv"],                       Icon: TableChartIcon,   color: "#10B981", label: "Spreadsheets" },
  { exts: ["json"],                                     Icon: DataObjectIcon,   color: "#3B82F6", label: "Data Files" },
  { exts: ["pdf"],                                      Icon: PictureAsPdfIcon, color: "#EF4444", label: "PDFs" },
  { exts: ["txt", "md", "log", "yml", "yaml", "html"], Icon: ArticleIcon,      color: "#64748B", label: "Documents" },
  { exts: ["zip", "tar", "gz"],                         Icon: FolderZipIcon,    color: "#F59E0B", label: "Archives" },
];

// Consistent group ordering
const GROUP_ORDER = ["Images", "Spreadsheets", "Data Files", "PDFs", "Documents", "Archives", "Other"];

function getFileTypeConfig(filename) {
  const ext = (filename || "").split(".").pop().toLowerCase();
  for (const rule of FILE_TYPE_RULES) {
    if (rule.exts.includes(ext)) return rule;
  }
  return { Icon: FileIcon, color: "#64748B", label: "Other" };
}

function getShortName(filepath) {
  return (filepath || "").split("/").pop();
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function MetaItem({ icon, label, value }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
      <Typography
        component="span"
        sx={{
          color: "#94A3B8",
          textTransform: "uppercase",
          letterSpacing: 0.8,
          fontSize: "0.62rem",
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          fontWeight: 600,
        }}
      >
        {icon && React.cloneElement(icon, { sx: { fontSize: 11, color: "#94A3B8" } })}
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          color: "#1E293B",
          fontSize: "0.78rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  );
}

function StatusBadge({ status }) {
  const cfg = getStatusConfig(status);
  const { Icon } = cfg;
  const lower = (status || "").toLowerCase();
  const isSpinning = lower.startsWith("process") || lower.startsWith("started");
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.6,
        px: 1.25,
        py: 0.4,
        borderRadius: 99,
        backgroundColor: cfg.bg,
      }}
    >
      {isSpinning ? (
        <Box
          component="img"
          src="/processing.gif"
          alt="Processing"
          sx={{ width: 14, height: 14, borderRadius: "50%" }}
        />
      ) : (
        <Icon sx={{ fontSize: 11, color: cfg.color }} />
      )}
      <Typography
        component="span"
        sx={{ color: cfg.color, fontWeight: 700, fontSize: "0.68rem", letterSpacing: 0.3 }}
      >
        {cfg.label}
      </Typography>
    </Box>
  );
}

const NON_VIEWABLE_EXTS = new Set(["zip", "tar", "gz", "tar.gz", "rar", "7z"]);

function isViewable(filename) {
  const ext = (filename || "").split(".").pop().toLowerCase();
  return !NON_VIEWABLE_EXTS.has(ext);
}

function FileRow({ filename, isSelected, onView, onDownload }) {
  const { Icon, color } = getFileTypeConfig(filename);
  const shortName = getShortName(filename);
  const canView = isViewable(filename);
  return (
    <Box
      onClick={canView ? onView : undefined}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 0.75,
        backgroundColor: isSelected ? "#EFF6FF" : "#fff",
        borderBottom: "1px solid #F1F5F9",
        "&:last-child": { borderBottom: "none" },
        cursor: canView ? "pointer" : "default",
        "&:hover": {
          backgroundColor: isSelected ? "#DBEAFE" : canView ? "#F0F7FF" : "#F8FAFC",
        },
        transition: "background-color 0.1s ease",
      }}
    >
      {/* File type icon */}
      <Icon sx={{ fontSize: 16, color, flexShrink: 0 }} />

      {/* Filename — truncated, never wraps */}
      <Typography
        variant="caption"
        sx={{
          flex: 1,
          fontFamily: "monospace",
          fontSize: "0.75rem",
          color: isSelected ? "#1E3A5F" : "#374151",
          fontWeight: isSelected ? 600 : 400,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
        }}
      >
        {shortName}
      </Typography>

      {/* Download button only */}
      <Tooltip title="Download file">
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onDownload(); }}
          sx={{
            flexShrink: 0,
            color: "#94A3B8",
            borderRadius: 1,
            p: 0.5,
            "&:hover": { color: "#2F3F5C", backgroundColor: "rgba(47,63,92,0.08)" },
          }}
        >
          <FileDownloadIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function SummaryPanel({ record, userId, hasSummaryData }) {
  const navigate = useNavigate();
  const duration = getDuration(record.start_datetime, record.end_datetime);
  const outputCount = Array.isArray(record.output_files) ? record.output_files.length : null;
  const inputLabel = Array.isArray(record.input_files)
    ? record.input_files.join(", ")
    : record.input_files || "—";

  const basedOn = record.input_definition_id
    ? {
        icon: <InputIcon />,
        label: "Based on",
        value: (
          <Box
            component="button"
            type="button"
            onClick={(e) => { e.stopPropagation(); navigate(`/input/${record.input_definition_id}`); }}
            sx={{
              background: "none", border: 0, p: 0, m: 0,
              color: "#1976d2", cursor: "pointer", fontFamily: "inherit",
              fontSize: "inherit", fontWeight: 500, textAlign: "left",
            }}
          >
            definition
          </Box>
        ),
      }
    : null;

  const metaItems = [
    { icon: <BiotechIcon />,      label: "Analysis",  value: productIdToTitle(record.product) },
    { icon: <PersonIcon />,       label: "Run by",    value: record.user_id },
    { icon: <CalendarTodayIcon />,label: "Started",   value: formatDate(record.start_datetime * 1000) },
    duration && { icon: <AccessTimeIcon />, label: "Duration", value: duration },
    { icon: <InputIcon />,        label: "Inputs",    value: inputLabel },
    basedOn,
    outputCount !== null && { icon: <BarChartIcon />, label: "Outputs", value: `${outputCount} files` },
  ].filter(Boolean);

  return (
    <Box>
      {/* Metadata card row */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
          gap: 1.5,
          mb: 3,
        }}
      >
        {metaItems.map((item, i) => (
          <Box
            key={i}
            sx={{
              p: 1.5,
              backgroundColor: "#fff",
              border: "1px solid #E2E8F0",
              borderRadius: 1.5,
              minWidth: 0,
            }}
          >
            <MetaItem icon={item.icon} label={item.label} value={item.value} />
          </Box>
        ))}
      </Box>

      {/* Slides or empty state */}
      {hasSummaryData === undefined ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
          <CircularProgress size={28} sx={{ color: "#2F3F5C" }} />
        </Box>
      ) : hasSummaryData ? (
        <PresentationViewer
          userId={userId}
          reportId={record.id}
          jsonFilename="summary_data.json"
          dataLabel="summary"
          onClose={() => {}}
        />
      ) : (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            border: "1px dashed #CBD5E1",
            borderRadius: 2,
            color: "#94A3B8",
          }}
        >
          <BarChartIcon sx={{ fontSize: 36, mb: 1.5, opacity: 0.35 }} />
          <Typography variant="body2" sx={{ color: "#94A3B8", fontWeight: 500 }}>
            No summary report available for this job
          </Typography>
          <Typography variant="caption" sx={{ color: "#CBD5E1", display: "block", mt: 0.5 }}>
            Switch to Outputs to browse result files
          </Typography>
        </Box>
      )}
    </Box>
  );
}

const AI_SKILL_OUTPUT_FILES = ["ai_skill_interpretation.md", "ai_skill_interpretation.json"];
const BIOINTERPRETER_OUTPUT_FILES = ["biointerpreter_response.md", "biointerpreter_response.pdf"];
// Named BioInterpreter variants for side-by-side comparison (see
// Package.biointerpreter_variants / biointerpreter_runner.py). Each variant's
// output files are suffixed -<name> so they never collide with the default's.
// Add an entry here for each new package.yml-defined variant.
const BIOINTERPRETER_VARIANTS = [
  {
    name: "20260902",
    label: "BioInterpreter-20260902",
    outputFiles: ["biointerpreter_response-20260902.md", "biointerpreter_response-20260902.pdf"],
  },
];

// Agent-harness interpreters (apps/continuous_mfa/agent_harness,
// docs/report-interpreter.md): a WORKFLOW.md run by a rented agent harness,
// started via /report-request {type: "interpret", agent, workflow}. Each
// (workflow, agent) pair is its own subtask `agent-<workflow>-<agent>`, and
// the bridge copies its outputs into the run dir suffixed -<agent>. Add an
// entry here for each workflow in agent_harness_bridge.WORKFLOWS.
const AGENT_INTERPRETERS = [
  {
    workflow: "interpret-report",
    agent: "claude_code",
    label: "Report Interpreter (Claude Code agent)",
    outputFiles: ["ai_skill_interpretation-claude_code.md", "ai_skill_interpretation-claude_code.json"],
  },
  {
    workflow: "biointerpret",
    agent: "claude_code",
    label: "BioInterpret (Claude Code agent)",
    outputFiles: ["biointerpret_report-claude_code.md", "biointerpret_claims-claude_code.json"],
  },
];

const agentTaskKey = (a) => `agent-${a.workflow}-${a.agent}`;

// What the last run of an agent cost (run.subtasks.<task>.usage).
function LastRunUsage({ task }) {
  const text = task?.status !== "running" ? formatUsage(task?.usage) : null;
  if (!text) return null;
  return (
    <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
      Last run: {text}
    </Typography>
  );
}

function ReportInterpreterButton({ record, files, isAdmin, interpreting, interpretError, onRun }) {
  if (!isAdmin) return null;
  const alreadyRun = AI_SKILL_OUTPUT_FILES.some((f) => files.includes(f));
  return (
    <Box>
      <Button
        variant="outlined"
        size="small"
        startIcon={interpreting ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
        disabled={interpreting}
        onClick={() => onRun(record.id)}
        sx={{ fontWeight: 600 }}
      >
        {interpreting ? "Running Report Interpreter…" : alreadyRun ? "Re-run Report Interpreter" : "Run Report Interpreter"}
      </Button>
      <LastRunUsage task={record.subtasks?.interpreter} />
      {interpretError && (
        <Alert severity="error" sx={{ mt: 1.5 }}>{interpretError}</Alert>
      )}
    </Box>
  );
}

function BioInterpreterButton({
  record,
  files,
  isAdmin,
  running,
  runError,
  onRun,
  label = "BioInterpreter",
  outputFiles = BIOINTERPRETER_OUTPUT_FILES,
  Icon = BiotechIcon,
  task = null,
}) {
  if (!isAdmin) return null;
  const alreadyRun = outputFiles.some((f) => files.includes(f));
  return (
    <Box>
      <Button
        variant="outlined"
        size="small"
        startIcon={running ? <CircularProgress size={14} /> : <Icon />}
        disabled={running}
        onClick={() => onRun(record.id)}
        sx={{ fontWeight: 600 }}
      >
        {running ? `Running ${label}…` : alreadyRun ? `Re-run ${label}` : `Run ${label}`}
      </Button>
      <LastRunUsage task={task} />
      {runError && (
        <Alert severity="error" sx={{ mt: 1.5 }}>{runError}</Alert>
      )}
    </Box>
  );
}

function OutputsPanel({
  record,
  files,
  selectedFile,
  onFileClick,
  onDirectDownload,
  isAdmin,
  interpreting,
  interpretError,
  onRunInterpreter,
  biointerpreterRunning,
  biointerpreterError,
  biointerpreterPostErrors,
  onRunBioInterpreter,
  agentPostErrors,
  onRunAgent,
}) {
  const interpreterButton = (
    <Stack spacing={1.5} sx={{ mb: 2.5 }}>
      <ReportInterpreterButton
        record={record}
        files={files}
        isAdmin={isAdmin}
        interpreting={interpreting}
        interpretError={interpretError}
        onRun={onRunInterpreter}
      />
      <BioInterpreterButton
        record={record}
        files={files}
        isAdmin={isAdmin}
        running={biointerpreterRunning}
        runError={biointerpreterError}
        onRun={onRunBioInterpreter}
      />
      {BIOINTERPRETER_VARIANTS.map((v) => (
        <BioInterpreterButton
          key={v.name}
          record={record}
          files={files}
          isAdmin={isAdmin}
          label={v.label}
          outputFiles={v.outputFiles}
          running={record.subtasks?.[`biointerpreter-${v.name}`]?.status === "running"}
          runError={
            (biointerpreterPostErrors || {})[`${record.id}:${v.name}`] ||
            (record.subtasks?.[`biointerpreter-${v.name}`]?.status === "error"
              ? record.subtasks[`biointerpreter-${v.name}`].error
              : null)
          }
          onRun={() => onRunBioInterpreter(record.id, v.name)}
        />
      ))}
      {AGENT_INTERPRETERS.map((a) => {
        const task = record.subtasks?.[agentTaskKey(a)];
        return (
          <BioInterpreterButton
            key={agentTaskKey(a)}
            record={record}
            files={files}
            isAdmin={isAdmin}
            label={a.label}
            outputFiles={a.outputFiles}
            Icon={SmartToyIcon}
            task={task}
            running={task?.status === "running"}
            runError={
              (agentPostErrors || {})[`${record.id}:${agentTaskKey(a)}`] ||
              (task?.status === "error" ? task.error : null)
            }
            onRun={() => onRunAgent(record.id, a)}
          />
        );
      })}
    </Stack>
  );

  if (files.length === 0) {
    return (
      <Box>
        {interpreterButton}
        <Box sx={{ textAlign: "center", py: 6, border: "1px dashed #CBD5E1", borderRadius: 2 }}>
          <FileIcon sx={{ fontSize: 36, mb: 1.5, opacity: 0.35, color: "#94A3B8" }} />
          <Typography variant="body2" sx={{ color: "#94A3B8" }}>No output files available</Typography>
        </Box>
      </Box>
    );
  }

  // Build ordered groups
  const groups = {};
  [...files].sort().forEach((f) => {
    const { label } = getFileTypeConfig(f);
    if (!groups[label]) groups[label] = [];
    groups[label].push(f);
  });
  const sortedGroups = GROUP_ORDER.filter((g) => groups[g]).map((g) => [g, groups[g]]);

  return (
    <Box>
      {interpreterButton}
      {sortedGroups.map(([groupLabel, groupFiles]) => (
        <Box key={groupLabel} sx={{ mb: 2.5 }}>
          <Typography
            component="div"
            sx={{
              color: "#94A3B8",
              textTransform: "uppercase",
              letterSpacing: 1.5,
              fontSize: "0.6rem",
              fontWeight: 700,
              mb: 0.75,
              px: 0.5,
            }}
          >
            {groupLabel} · {groupFiles.length}
          </Typography>
          <Box sx={{ border: "1px solid #E2E8F0", borderRadius: 1.5, overflow: "hidden", backgroundColor: "#fff" }}>
            {groupFiles.map((file, i) => (
              <FileRow
                key={i}
                filename={file}
                isSelected={selectedFile === file}
                onView={() => onFileClick(record.id, file)}
                onDownload={() => onDirectDownload(record.id, file)}
              />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function ReportPanel({ record, userId, files }) {
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportContent, setReportContent] = useState(null);

  const reportFile = (files || []).find(
    (file) => getShortName(file).toLowerCase() === "report.md"
  );

  useEffect(() => {
    let cancelled = false;

    const loadReport = async () => {
      if (!reportFile) {
        setReportContent(null);
        setLoadingReport(false);
        return;
      }

      setLoadingReport(true);
      try {
        const response = await apiClient.getRaw(
          `/report-path/${userId}/work/${record.id}/${reportFile}`
        );
        if (!cancelled) {
          setReportContent(typeof response === "string" ? response : JSON.stringify(response, null, 2));
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching report.md:", error);
          setReportContent("Error loading report.md.");
        }
      } finally {
        if (!cancelled) {
          setLoadingReport(false);
        }
      }
    };

    loadReport();

    return () => {
      cancelled = true;
    };
  }, [record.id, reportFile, userId]);

  if (!reportFile) {
    return (
      <Box sx={{ textAlign: "center", py: 6, border: "1px dashed #CBD5E1", borderRadius: 2 }}>
        <ArticleIcon sx={{ fontSize: 36, mb: 1.5, opacity: 0.35, color: "#94A3B8" }} />
        <Typography variant="body2" sx={{ color: "#94A3B8" }}>
          No report.md file available in Outputs
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ border: "1px solid #E2E8F0", borderRadius: 1.5, overflow: "hidden", backgroundColor: "#fff" }}>
      <Box sx={{ p: 2 }}>
        <FileViewer
          fileContent={reportContent}
          selectedFile={reportFile}
          loading={loadingReport}
        />
      </Box>
    </Box>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────────────

export default function ReportTableBody({
  userId,
  staff = false,
  onMoveProject = null,
  reports,
  onOpenDeleteDialog,
  onOpenCancelDialog,
  setZoomedImage,
  deletingReportId,
  cancellingReportId,
  refresh,
}) {
  const [expandedRows, setExpandedRows] = useState({});
  // "Running" state now lives on the row itself (record.subtasks.interpreter/
  // .biointerpreter — see subtask_util.py) so it survives a refresh and is
  // visible to any viewer, not just the tab that clicked the button. These
  // two maps only hold the immediate rejection of the POST itself (403/404/
  // 409 — e.g. "already running"), which happens before a subtask ever gets
  // created and so has nowhere else to live.
  const [interpretPostErrors, setInterpretPostErrors] = useState({});
  const [biointerpreterPostErrors, setBiointerpreterPostErrors] = useState({});
  const [agentPostErrors, setAgentPostErrors] = useState({});
  const [activeTab, setActiveTab] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloadingReportId, setDownloadingReportId] = useState(null);
  const [packageYmlContent, setPackageYmlContent] = useState(null);
  const [hasSummaryData, setHasSummaryData] = useState({});
  const [hasLongData, setHasLongData] = useState({});
  const userInfo = useAuthStore((state) => state.user);
  const isAdmin = userInfo?.roles?.includes("Admin");
  const isInternalUser = userInfo?.roles?.includes("InternalUser");
  const { reportId } = useParams();
  // One user's list, or (project mode, no userId) every row is its owner's.
  const ownerOf = (id) => userId || reports.find((r) => String(r.id) === String(id))?.user_id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (reportId && reports.length > 0) {
      const matchingReport = reports.find(
        (record) => String(record.id) === String(reportId)
      );
      if (matchingReport && !expandedRows[matchingReport.id]) {
        setExpandedRows({ [matchingReport.id]: true });
        handleReportSelection(matchingReport.id);
        // Deep-link support: Evaluations links to a report with
        // ?tab=BioInterpreter so a reviewer lands straight on the grading
        // tab instead of the default "Report" tab. Falls back to "Report"
        // on its own if the tab name is invalid/unavailable (same
        // resolution used when a tab disappears — see tabs.includes check
        // below).
        const requestedTab = searchParams.get("tab");
        if (requestedTab) {
          setActiveTab((prev) => ({ ...prev, [matchingReport.id]: requestedTab }));
        }
      }
    }
  }, [reportId, reports, expandedRows, searchParams]);

  const handleReportSelection = async (id) => {
    try {
      const response = await apiClient.getRaw(
        `/report-path/${ownerOf(id)}/work/${id}/package.yml`
      );
      const parsedYaml = yaml.load(response);
      setPackageYmlContent(parsedYaml);

      try {
        await apiClient.getRaw(
          `/report-path/${ownerOf(id)}/work/${id}/final_outputs/summary_data.json`
        );
        setHasSummaryData((prev) => ({ ...prev, [id]: true }));
      } catch {
        setHasSummaryData((prev) => ({ ...prev, [id]: false }));
      }

      try {
        await apiClient.getRaw(
          `/report-path/${ownerOf(id)}/work/${id}/final_outputs/long_data.json`
        );
        setHasLongData((prev) => ({ ...prev, [id]: true }));
      } catch {
        setHasLongData((prev) => ({ ...prev, [id]: false }));
      }
    } catch (error) {
      console.error("Error fetching package.yml:", error);
      setPackageYmlContent("Error loading package.yml.");
    }
  };

  const getFilteredFiles = (record) => {
    if (isAdmin) return record.output_files || [];
    const archiveName = isInternalUser ? "internal_user_archive" : "user_archive";
    const archiveSet = packageYmlContent?.archive_sets?.find(
      (a) => a.name === archiveName
    );
    if (!archiveSet) return [];
    return (record.output_files || []).filter((file) =>
      archiveSet.files.includes(`./${file}`)
    );
  };

  const toggleRow = (id) => {
    setSelectedFile(null);
    setExpandedRows((prev) => {
      if (prev[id]) {
        if (userId) navigate(`/report-list/${userId}`);
        return {};
      }
      handleReportSelection(id);
      // Default landing tab is Report. Kept as a name (not index) so an
      // async tab insertion later (Details) can't shift what "the first
      // tab" resolves to.
      setActiveTab((prevTab) => ({ ...prevTab, [id]: "Report" }));
      if (userId) navigate(`/report-list/${userId}/${id}`);
      return { [id]: true };
    });
  };

  const handleTabChange = (id, newValue) => {
    // newValue is the tab NAME (matches the value= prop on each <Tab />).
    setActiveTab((prev) => ({ ...prev, [id]: newValue }));
  };

  const handleFileClick = async (id, file) => {
    setSelectedFile(file);
    setFileContent(null);
    setLoading(true);
    setViewerOpen(true);
    try {
      const response = await apiClient.getRaw(
        `/report-path/${ownerOf(id)}/work/${id}/${file}`
      );
      const ext = file.split(".").pop().toLowerCase();
      setFileContent(ext === "json" ? JSON.stringify(response, null, 2) : response);
    } catch (error) {
      console.error("Error fetching file:", error);
      setFileContent("Error loading file.");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    setSelectedFile(null);
    setFileContent(null);
    setCopied(false);
  };

  const handleCopy = async () => {
    if (typeof fileContent !== "string") return;
    try {
      await navigator.clipboard.writeText(fileContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  const handleDownloadContents = () => {
    const element = document.createElement("a");
    const file = new Blob([fileContent], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = selectedFile;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  // Direct download without opening viewer
  const handleDirectDownload = async (id, file) => {
    try {
      const response = await apiClient.getRaw(
        `/report-path/${ownerOf(id)}/work/${id}/${file}`
      );
      const shortName = getShortName(file);
      let blob;
      if (response instanceof Blob) {
        blob = response;
      } else if (typeof response === "string") {
        blob = new Blob([response], { type: "text/plain" });
      } else {
        blob = new Blob([JSON.stringify(response, null, 2)], { type: "application/json" });
      }
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.setAttribute("download", shortName);
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(link.href);
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  // `archiveType` lets a caller force which download this click performs —
  // "full" (Admin-only, the whole work directory) or an explicit
  // package.yml archive_set name ("user_archive" / "internal_user_archive").
  // Omitted, it falls back to what this user's role would get by default
  // (used by the lone non-admin button, which has no choice to make).
  const handleDownload = async (id, record, archiveType) => {
    // Keyed by id+type (not just id) so an Admin's two download buttons on
    // the same row show independent spinners instead of both lighting up.
    const type =
      archiveType || (isAdmin ? "full" : isInternalUser ? "internal_user_archive" : "user_archive");
    setDownloadingReportId(`${id}:${type}`);
    try {
      let baseName = id;
      if (record.input_files && record.input_files.length > 0) {
        baseName = record.input_files[0].replace(/\.[^/.]+$/, "");
      }
      if (type === "full") {
        const blob = await apiClient.postRaw(
          "/report-request",
          { type: "zip", report_id: id, user_id: ownerOf(id) },
          "blob"
        );
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.setAttribute("download", `${baseName}-full_report.zip`);
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(link.href);
        document.body.removeChild(link);
        return;
      }
      const archiveName = type;
      const response = await apiClient.getRaw(
        `/report/${id}-${archiveName}.zip?user_id=${ownerOf(id)}`,
        { responseType: "blob" }
      );
      const link = document.createElement("a");
      link.href = URL.createObjectURL(response);
      link.setAttribute("download", `${baseName}-${archiveName}.zip`);
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(link.href);
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading file:", error);
    } finally {
      setDownloadingReportId(null);
    }
  };

  const handleRunAgent = async (id, a) => {
    const errorKey = `${id}:${agentTaskKey(a)}`;
    setAgentPostErrors((prev) => ({ ...prev, [errorKey]: null }));
    try {
      await apiClient.postRaw("/report-request", {
        type: "interpret",
        report_id: id,
        user_id: ownerOf(id),
        agent: a.agent,
        workflow: a.workflow,
      });
      if (refresh) await refresh();
    } catch (error) {
      console.error(`Error starting ${a.label}:`, error);
      setAgentPostErrors((prev) => ({ ...prev, [errorKey]: error.message || `Failed to start ${a.label}.` }));
    }
  };

  const handleRunInterpreter = async (id) => {
    setInterpretPostErrors((prev) => ({ ...prev, [id]: null }));
    try {
      await apiClient.postRaw("/report-request", { type: "interpret", report_id: id, user_id: ownerOf(id) });
      // The POST now returns as soon as the subtask is marked "running" —
      // refresh right away so the row's real subtasks.interpreter.status
      // shows up without waiting for the table's next poll tick.
      if (refresh) await refresh();
    } catch (error) {
      console.error("Error starting report interpreter:", error);
      setInterpretPostErrors((prev) => ({ ...prev, [id]: error.message || "Failed to start report interpreter." }));
    }
  };

  // variant: undefined/null runs the default BioInterpreter; a name (e.g.
  // "20260902") runs that package.yml-defined comparison variant instead
  // (own input archive, prompt, model, non-colliding output — see
  // biointerpreter_runner.py). Each variant tracks its own post-error under
  // a composite key so the default and every variant stay independent.
  const handleRunBioInterpreter = async (id, variant) => {
    const errorKey = variant ? `${id}:${variant}` : id;
    setBiointerpreterPostErrors((prev) => ({ ...prev, [errorKey]: null }));
    try {
      await apiClient.postRaw("/report-request", {
        type: "run_biointerpreter",
        report_id: id,
        user_id: ownerOf(id),
        ...(variant ? { variant } : {}),
      });
      if (refresh) await refresh();
    } catch (error) {
      console.error("Error starting BioInterpreter:", error);
      setBiointerpreterPostErrors((prev) => ({
        ...prev,
        [errorKey]: error.message || "Failed to start BioInterpreter.",
      }));
    }
  };

  return (
    <TableBody>
      {reports.map((record) => {
        const isExpanded = !!expandedRows[record.id];
        // Track the active tab by NAME, not index. Details is an async
        // insertion (PPTX conversion + long_data.json check finish after
        // the row expands). Tracking by index means a mid-list insertion
        // shifts every later tab by one — the user's BioInterpreter panel
        // unmounts and the PresentationViewer takes its slot, wiping the
        // in-progress evaluation form state. Tracking by name keeps the
        // active tab stable regardless of insertion order.
        const activeName = activeTab[record.id];

        const inputFiles = Array.isArray(record.input_files)
          ? record.input_files.filter(Boolean)
          : record.input_files
            ? [record.input_files]
            : [];
        const inputLabel = inputFiles.length ? inputFiles.join(", ") : "—";
        const inputTooltip = inputFiles.length ? inputFiles.join("\n") : "No input files";

        const duration = getDuration(record.start_datetime, record.end_datetime);

        // Build tab list — Report first, then Outputs, Summary, and Details
        const tabs = [];
        const tabContent = [];

        const filteredFiles = getFilteredFiles(record);

        tabs.push("Report");
        tabContent.push(
          <ReportPanel
            key="report"
            record={record}
            userId={userId || record.user_id}
            files={filteredFiles}
          />
        );

        tabs.push("Outputs");
        tabContent.push(
          <OutputsPanel
            key="outputs"
            record={record}
            files={filteredFiles}
            selectedFile={selectedFile}
            onFileClick={handleFileClick}
            onDirectDownload={handleDirectDownload}
            isAdmin={isAdmin}
            interpreting={record.subtasks?.interpreter?.status === "running"}
            interpretError={
              interpretPostErrors[record.id] ||
              (record.subtasks?.interpreter?.status === "error" ? record.subtasks.interpreter.error : null)
            }
            onRunInterpreter={handleRunInterpreter}
            biointerpreterRunning={record.subtasks?.biointerpreter?.status === "running"}
            biointerpreterError={
              biointerpreterPostErrors[record.id] ||
              (record.subtasks?.biointerpreter?.status === "error" ? record.subtasks.biointerpreter.error : null)
            }
            biointerpreterPostErrors={biointerpreterPostErrors}
            onRunBioInterpreter={handleRunBioInterpreter}
            agentPostErrors={agentPostErrors}
            onRunAgent={handleRunAgent}
          />
        );

        tabs.push("Summary");
        tabContent.push(
          <SummaryPanel
            key="summary"
            record={record}
            userId={userId || record.user_id}
            hasSummaryData={hasSummaryData[record.id]}
          />
        );

        if (hasLongData[record.id]) {
          tabs.push("Details");
          tabContent.push(
            <PresentationViewer
              key="details"
              userId={userId || record.user_id}
              reportId={record.id}
              jsonFilename="long_data.json"
              dataLabel="long"
              onClose={() => {}}
            />
          );
        }

        // BioInterpreter evaluation tabs — only when this run was
        // BioInterpreter-enabled AND the viewer is Admin or InternalUser.
        // Backend router additionally enforces the role gate, so hiding
        // here is UX only, not a security boundary. Each tab is a
        // self-contained panel (response viewer + evaluation log + grading
        // form) for ONE variant, with its own independent evaluation
        // envelope — there is no separate "Evaluation" tab. Gated only on
        // canGradeBioInterpreter, not on whether that variant's .md has
        // been generated yet: checking the single run.evaluate box is what
        // surfaces both evaluations, per design — the panel itself shows
        // "No content available" until the response exists, same as the
        // default variant always has.
        const canGradeBioInterpreter = (isAdmin || isInternalUser) && !!record.use_biointerpreter;
        if (canGradeBioInterpreter) {
          tabs.push("BioInterpreter");
          tabContent.push(
            <BioInterpreterEvaluationPanel
              key="bio-interpreter"
              record={record}
              userId={userId || record.user_id}
            />
          );
          // One tab per named BioInterpreter variant (see BIOINTERPRETER_VARIANTS
          // above), e.g. "BioInterpreter-20260902" — independent envelope/log
          // from the default variant's, via BioInterpreterEvaluationPanel's
          // `variant` prop.
          BIOINTERPRETER_VARIANTS.forEach((v) => {
            const mdFile = v.outputFiles.find((f) => f.endsWith(".md"));
            tabs.push(v.label);
            tabContent.push(
              <BioInterpreterEvaluationPanel
                key={`bio-interpreter-${v.name}`}
                record={record}
                userId={userId || record.user_id}
                variant={v.name}
                mdFile={mdFile}
              />
            );
          });
        }

        // Resolve the active tab to an index for content lookup. Falls back
        // to 0 if the active tab is unknown (e.g. it disappeared because the
        // role gate flipped) or unset (first render). Never uses the shifted
        // index of a mid-list insertion.
        const activeIdx = activeName && tabs.includes(activeName)
          ? tabs.indexOf(activeName)
          : 0;
        const currentTabName = tabs[activeIdx];

        return (
          <React.Fragment key={record.id}>
            {/* ── Main Row ─────────────────────────────────────────────── */}
            <TableRow
              sx={{
                backgroundColor: isExpanded ? "#EFF6FF" : "inherit",
                "&:hover": { backgroundColor: "#F8FAFC" },
                cursor: "pointer",
                "& td": { borderBottom: isExpanded ? "none" : undefined },
              }}
              onClick={() => toggleRow(record.id)}
            >
              {/* Expand toggle */}
              <TableCell sx={{ width: 44, px: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); toggleRow(record.id); }}
                  sx={{ color: isExpanded ? "#2F3F5C" : "#94A3B8" }}
                >
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </TableCell>

              {/* Product */}
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#1E293B", fontSize: "0.82rem" }}>
                  {productIdToTitle(record.product)}
                </Typography>
                {!userId && (
                  <Typography variant="caption" sx={{ color: "#64748B", display: "block" }}>by {record.user_id}</Typography>
                )}
                {(record.project_tag || staff) && (
                  <Typography variant="caption" sx={{ color: "#475569", display: "block" }}>
                    {staff
                      ? (record.project_id ? `${record.project_id} · ${record.project_tag || ""}` : "Not in a project")
                      : `#${record.project_tag}${record.project_status ? ` · ${record.project_status}` : ""}`}
                  </Typography>
                )}
                {onMoveProject && (
                  <Button size="small" sx={{ p: 0, minWidth: 0, fontSize: "0.7rem", textTransform: "none" }}
                          onClick={(e) => { e.stopPropagation(); onMoveProject(record); }}>
                    {record.project_tag || record.project_id ? "Move to another project" : "Put in a project"}
                  </Button>
                )}
              </TableCell>

              {/* Status */}
              <TableCell>
                <StatusBadge status={record.status} />
                {record.status === "error" && record.exception && isAdmin && (
                  <Tooltip title={record.exception}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "#EF4444",
                        mt: 0.25,
                        maxWidth: 200,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        cursor: "help",
                      }}
                    >
                      {record.exception}
                    </Typography>
                  </Tooltip>
                )}
              </TableCell>

              {/* Input files */}
              <TableCell>
                <Tooltip
                  title={inputTooltip}
                  placement="top-start"
                  arrow
                  slotProps={{
                    tooltip: {
                      sx: {
                        maxWidth: 520,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        fontFamily: "monospace",
                        fontSize: "0.72rem",
                        lineHeight: 1.45,
                      },
                    },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#475569",
                      fontFamily: "monospace",
                      fontSize: "0.72rem",
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: 240,
                      cursor: "help",
                    }}
                  >
                    {inputLabel}
                  </Typography>
                </Tooltip>
              </TableCell>

              {/* Date + duration */}
              <TableCell>
                <Typography variant="caption" sx={{ color: "#475569", display: "block" }}>
                  {formatDate(record.start_datetime * 1000)}
                </Typography>
                {duration && (
                  <Typography variant="caption" sx={{ color: "#94A3B8", display: "block" }}>
                    {duration}
                  </Typography>
                )}
              </TableCell>

              {/* Actions */}
              <TableCell
                sx={{ whiteSpace: "nowrap" }}
                onClick={(e) => e.stopPropagation()}
              >
                <Tooltip title={isAdmin ? "Download full report (everything)" : "Download results"}>
                  {downloadingReportId === `${record.id}:${isAdmin ? "full" : isInternalUser ? "internal_user_archive" : "user_archive"}` ? (
                    <CircularProgress size={16} sx={{ mx: 1.25, my: 0.75, color: "#2F3F5C" }} />
                  ) : (
                    <IconButton
                      size="small"
                      onClick={() => handleDownload(record.id, record, isAdmin ? "full" : undefined)}
                      sx={{ color: "#2F3F5C" }}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  )}
                </Tooltip>
                {isAdmin && (
                  // Admins also see what a regular (non-Admin, non-InternalUser)
                  // user gets for this report — the smaller user_archive.zip
                  // subset — without having to log in as that user to check.
                  <Tooltip title="Download user archive (what a regular user sees)">
                    {downloadingReportId === `${record.id}:user_archive` ? (
                      <CircularProgress size={16} sx={{ mx: 1.25, my: 0.75, color: "#2F3F5C" }} />
                    ) : (
                      <IconButton
                        size="small"
                        onClick={() => handleDownload(record.id, record, "user_archive")}
                        sx={{ color: "#2F3F5C" }}
                      >
                        <PersonIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Tooltip>
                )}
                {!TERMINAL_REPORT_STATUSES.has((record.status || "").toLowerCase()) && (
                  <Tooltip title="Cancel run">
                    {cancellingReportId === record.id ? (
                      <CircularProgress size={16} sx={{ mx: 1.25, my: 0.75, color: "#94A3B8" }} />
                    ) : (
                      <IconButton
                        size="small"
                        onClick={() => onOpenCancelDialog(record.id)}
                        sx={{ color: "#94A3B8", "&:hover": { color: "#F59E0B" } }}
                      >
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Tooltip>
                )}
                <Tooltip title="Delete report">
                  {deletingReportId === record.id ? (
                    <CircularProgress size={16} sx={{ mx: 1.25, my: 0.75, color: "#94A3B8" }} />
                  ) : (
                    <IconButton
                      size="small"
                      onClick={() => onOpenDeleteDialog(record.id)}
                      sx={{ color: "#94A3B8", "&:hover": { color: "#EF4444" } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Tooltip>
              </TableCell>
            </TableRow>

            {/* ── Expanded Panel ────────────────────────────────────────── */}
            <TableRow>
              <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box
                    sx={{
                      backgroundColor: "#F8FAFC",
                      borderTop: "2px solid #2F3F5C",
                      borderBottom: "1px solid #E2E8F0",
                    }}
                  >
                    {/* Section header + tabs */}
                    <Box sx={{ px: 3, pt: 2.5, pb: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                        <Typography
                          component="span"
                          sx={{
                            color: "#2F3F5C",
                            fontWeight: 800,
                            letterSpacing: 2,
                            fontSize: "0.62rem"
                          }}
                        >
                          ID
                        </Typography>
                        <Typography
                          sx={{ color: "#94A3B8", fontSize: "0.68rem", fontFamily: "monospace" }}
                        >
                          {record.id}
                        </Typography>
                      </Box>

                      <Tabs
                        value={currentTabName}
                        onChange={(_, v) => handleTabChange(record.id, v)}
                        sx={{
                          minHeight: 36,
                          "& .MuiTabs-indicator": { backgroundColor: "#2F3F5C", height: 2 },
                          "& .MuiTab-root": {
                            minHeight: 36,
                            fontSize: "0.78rem",
                            fontWeight: 500,
                            color: "#64748B",
                            textTransform: "none",
                            px: 2,
                            "&.Mui-selected": { color: "#2F3F5C", fontWeight: 700 },
                          },
                        }}
                      >
                        {tabs.map((label) => (
                          <Tab key={label} value={label} label={label} />
                        ))}
                      </Tabs>
                    </Box>

                    {/* Tab content */}
                    <Box sx={{ px: 3, py: 3 }}>
                      {tabContent[activeIdx]}
                    </Box>
                  </Box>
                </Collapse>
              </TableCell>
            </TableRow>
          </React.Fragment>
        );
      })}

      {/* ── File viewer modal ──────────────────────────────────────────── */}
      <Dialog
        open={viewerOpen}
        onClose={handleCloseViewer}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            py: 1.5,
            px: 2.5,
            borderBottom: "1px solid #E2E8F0",
            backgroundColor: "#F8FAFC",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
            {selectedFile && (() => {
              const { Icon, color } = getFileTypeConfig(selectedFile);
              return <Icon sx={{ fontSize: 18, color, flexShrink: 0 }} />;
            })()}
            <Typography
              sx={{
                fontFamily: "monospace",
                fontSize: "0.88rem",
                fontWeight: 600,
                color: "#1E293B",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {selectedFile ? getShortName(selectedFile) : ""}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
            <Tooltip title={typeof fileContent !== "string" ? "Cannot copy binary file" : copied ? "Copied!" : "Copy to clipboard"}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleCopy}
                  disabled={typeof fileContent !== "string" || loading}
                  sx={{
                    color: copied ? "#10B981" : "#2F3F5C",
                    "&.Mui-disabled": { color: "#CBD5E1" },
                  }}
                >
                  {copied
                    ? <CheckIcon sx={{ fontSize: 18 }} />
                    : <CopyIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Download file">
              <IconButton size="small" onClick={handleDownloadContents} sx={{ color: "#2F3F5C" }}>
                <FileDownloadIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton size="small" onClick={handleCloseViewer} sx={{ color: "#64748B" }}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2.5, overflow: "auto" }}>
          {viewerOpen && (
            <FileViewer
              fileContent={fileContent}
              selectedFile={selectedFile}
              loading={loading}
            />
          )}
        </DialogContent>
      </Dialog>
    </TableBody>
  );
}
